import { ResponseArtifactDTO } from '@dreamai/types';
import api from '../api';
import { getFlightOffer, isFlightsView } from './chat/sidebar/flightArtifact';

/** Fares this close to expiry are refreshed too, so Book does not race them. */
const EXPIRY_MARGIN_MS = 2 * 60 * 1000;
const RETRY_DELAY_MS = 2000;
const MAX_ATTEMPTS = 15;

/** Whether one choice has a fare that is expired or close to expiry. */
export function choiceNeedsLivePrices(
  choice: unknown,
  now = Date.now(),
): boolean {
  if (typeof choice !== 'object' || choice === null) return false;
  return (
    getFlightOffer(choice as Parameters<typeof getFlightOffer>[0])?.fares ?? []
  ).some((fare) => {
    const expiresAt = fare.expiresAt ? Date.parse(fare.expiresAt) : NaN;
    return !Number.isFinite(expiresAt) || expiresAt <= now + EXPIRY_MARGIN_MS;
  });
}

/** An open flights list whose fares have expired needs "Getting live prices". */
export function needsLivePrices(
  artifact: ResponseArtifactDTO,
  now = Date.now(),
): boolean {
  if (!isFlightsView(artifact.view) || artifact.status !== 'pending') {
    return false;
  }
  return (artifact.choices ?? []).some((choice) =>
    choiceNeedsLivePrices(choice, now),
  );
}

export function isLivePricesRequired(err: any): boolean {
  return (
    err?.response?.status === 410 &&
    err.response.data?.code === 'LIVE_PRICES_REQUIRED'
  );
}

/** Re-prices the list (and renews an expired link); waits out a refresh already running. */
export async function refreshLivePrices(shareToken: string): Promise<void> {
  for (let attempt = 1; ; attempt += 1) {
    try {
      await api.post(`/public/artifacts/${shareToken}/live-prices`);
      return;
    } catch (err: any) {
      if (err?.response?.status !== 409 || attempt >= MAX_ATTEMPTS) throw err;
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
    }
  }
}
